// firebaseHooks.ts - Updated to handle all channel properties

import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../firebase";

// Complete Channel Data interface with all properties
export interface ChannelData {
  allocated?: number;
  produced?: number;
  reserved?: number;
  cancelled?: number;
  available?: number;
  sold?: number;
  // Add any other possible channel properties
}

export interface CartData {
  cartId: string;
  cartName: string;
  channels: {
    [channelName: string]: ChannelData;
  };
}

export interface ProductSummary {
  id: string;
  itemId: string;
  productId: string;
  displayName: string;
  carts: {
    [cartId: string]: CartData;
  };
  // Channels aggregated across all carts for this product
  channels: {
    [channelName: string]: ChannelData;
  };
  updatedAt?: string;
}

export interface UseFirebaseProductSummariesResult {
  data: ProductSummary[] | null;
  loading: boolean;
  error: Error | null;
}

// Helper function to extract number values from Firestore data
const extractNumberValue = (data: any, fieldName: string): number => {
  if (!data || !data[fieldName]) return 0;
  
  const value = data[fieldName];
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseInt(value, 10);
  if (value.integerValue !== undefined) return parseInt(value.integerValue, 10);
  if (value.doubleValue !== undefined) return parseFloat(value.doubleValue);
  return 0;
};

// Helper function to extract nested Firestore map values
const extractMapValue = (data: any): any => {
  if (!data) return {};
  if (data.mapValue && data.mapValue.fields) return data.mapValue.fields;
  if (typeof data === 'object' && !Array.isArray(data)) return data;
  return {};
};

export const useFirebaseProductSummaries = (
  restaurantId: string,
  date?: Date
): UseFirebaseProductSummariesResult => {
  const [data, setData] = useState<ProductSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      setData(null);
      return;
    }

    setLoading(true);

    // Format date as YYYYMMDD
    const targetDate = date || new Date();
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const dateString = `${year}${month}${day}`;

    const productSummariesDocRef = doc(
      db,
      'Restaurants',
      restaurantId,
      'inventories',
      dateString,
      'projections',
      'productSummaries'
    );

    const unsubscribe = onSnapshot(
      productSummariesDocRef,
      (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const docData = docSnapshot.data();

            let productsArray: ProductSummary[] = [];

            if (docData && docData.products) {
              const productsMap = extractMapValue(docData.products);
              
              // Convert the products object to an array of ProductSummary objects
              productsArray = Object.keys(productsMap).map(productKey => {
                const productData = productsMap[productKey];
                const itemData = extractMapValue(productData);
                
                // Parse carts object
                const rawCarts = extractMapValue(itemData.carts);
                const carts: ProductSummary['carts'] = {};
                
                // Initialize aggregated channels for this product
                const aggregatedChannels: { [channelName: string]: ChannelData } = {};
                
                Object.keys(rawCarts).forEach(cartKey => {
                  const cartData = extractMapValue(rawCarts[cartKey]);
                  
                  // Extract cart info
                  carts[cartKey] = {
                    cartId: cartData.cartId || cartKey,
                    cartName: cartData.cartName || '',
                    channels: {}
                  };
                  
                  // Extract channels from this cart
                  const cartChannels = extractMapValue(cartData.channels);
                  
                  Object.keys(cartChannels).forEach(channelKey => {
                    const channelData = extractMapValue(cartChannels[channelKey]);
                    
                    // Create channel data for this cart
                    const cartChannelData: ChannelData = {
                      allocated: extractNumberValue(channelData, 'allocated'),
                      reserved: extractNumberValue(channelData, 'reserved'),
                      cancelled: extractNumberValue(channelData, 'cancelled'),
                      produced: extractNumberValue(channelData, 'produced'),
                      available: extractNumberValue(channelData, 'available'),
                      sold: extractNumberValue(channelData, 'sold')
                    };
                    
                    // Add to cart's channels
                    carts[cartKey].channels[channelKey] = cartChannelData;
                    
                    // Aggregate across all carts for this product
                    if (!aggregatedChannels[channelKey]) {
                      aggregatedChannels[channelKey] = {
                        allocated: 0,
                        reserved: 0,
                        cancelled: 0,
                        produced: 0,
                        available: 0,
                        sold: 0
                      };
                    }
                    
                    // Sum up all values
                    aggregatedChannels[channelKey].allocated! += cartChannelData.allocated || 0;
                    aggregatedChannels[channelKey].reserved! += cartChannelData.reserved || 0;
                    aggregatedChannels[channelKey].cancelled! += cartChannelData.cancelled || 0;
                    aggregatedChannels[channelKey].produced! += cartChannelData.produced || 0;
                    aggregatedChannels[channelKey].available! += cartChannelData.available || 0;
                    aggregatedChannels[channelKey].sold! += cartChannelData.sold || 0;
                  });
                });

                // Filter out channels with all zero values (optional)
                const finalChannels: ProductSummary['channels'] = {};
                Object.keys(aggregatedChannels).forEach(channelKey => {
                  const channel = aggregatedChannels[channelKey];
                  // Only include channels that have at least one non-zero value
                  if (Object.values(channel).some(value => value !== 0)) {
                    finalChannels[channelKey] = channel;
                  }
                });

                return {
                  id: productKey,
                  itemId: itemData.itemId || productKey,
                  productId: itemData.productId || '',
                  displayName: itemData.displayName || '',
                  carts,
                  channels: finalChannels,
                  updatedAt: itemData.updatedAt
                };
              });

            } else {
              // console.log('No products data found in product summaries');
            }

            setData(productsArray);
          } else {
            setData([]);
          }
          setLoading(false);
          setError(null);
        } catch (err) {
          console.error('Error parsing product summaries document:', err);
          setError(err as Error);
          setLoading(false);
          setData([]);
        }
      },
      (err) => {
        console.error('Error fetching product summaries document:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, date?.getTime()]);

  return { data, loading, error };
};

// Helper functions to extract specific values

// Get a specific channel property for a product
export const getChannelValue = (
  productSummary: ProductSummary,
  channelName: string,
  property: keyof ChannelData
): number => {
  return productSummary.channels[channelName]?.[property] || 0;
};

// Get all channel properties for a specific channel
export const getChannelData = (
  productSummary: ProductSummary,
  channelName: string
): ChannelData => {
  return productSummary.channels[channelName] || {};
};

// Get cart-specific channel data
export const getCartChannelData = (
  productSummary: ProductSummary,
  cartId: string,
  channelName: string
): ChannelData => {
  const cart = productSummary.carts[cartId];
  if (!cart || !cart.channels) return {};
  return cart.channels[channelName] || {};
};

// Get total across all channels for a specific property
export const getTotalForProperty = (
  productSummary: ProductSummary,
  property: keyof ChannelData
): number => {
  return Object.values(productSummary.channels).reduce(
    (total, channel) => total + (channel[property] || 0),
    0
  );
};

// Get all unique channel names across all products
export const getAllChannelNames = (products: ProductSummary[]): string[] => {
  const channelNames = new Set<string>();
  products.forEach(product => {
    Object.keys(product.channels).forEach(channel => channelNames.add(channel));
  });
  return Array.from(channelNames);
};

// Get cart names
export const getCartNames = (productSummary: ProductSummary): string[] => {
  return Object.values(productSummary.carts).map(cart => cart.cartName);
};